import { useId } from "react";

/**
 * De gewichtsgrafiek van het dieroverzicht. Wat je er in één oogopslag uit moet halen: gaat het dier
 * omhoog of omlaag, en wat woog het laatst.
 *
 * <p>De tijdas loopt van de eerste tot de laatste meting binnen de gekozen periode, niet over de hele
 * periode: bij metingen in mei en juni stond de lijn anders samengeperst tussen lege maanden. Is er in die
 * periode niet gewogen, dan toont de grafiek wat er wél is — een leeg vak zegt niets, en een dier dat in 2018
 * voor het laatst op de weegschaal stond heeft nog steeds een verleden. De as schrijft er dan jaartallen bij. De
 * gewichtsas krijgt ronde stappen (een halve kilo tot vijftig) die bij de spreiding passen, met de eenheid
 * erboven. De laatste meting is gemarkeerd en draagt zijn waarde; bij elk punt staan datum en gewicht als
 * je eroverheen beweegt.
 */
export interface GewichtPunt {
  /** yyyy-MM-dd */
  datum: string;
  kg: number;
}

interface Props {
  punten: GewichtPunt[];
  /** yyyy-MM-dd: de periode loopt `maanden` maanden terug tot en met deze maand. */
  tot: string;
  maanden: number;
  locale: string;
  label: string;
  /** Wat er staat als er nooit gewogen is. */
  leegTekst?: string;
  /** Wat erbij staat als de grafiek buiten de gekozen periode moest kijken. */
  buitenPeriodeTekst?: string;
}

const B = 300;
const H = 150;
const LINKS = 30;
const RECHTS = 16;
const BOVEN = 20;
const ONDER = 22;
const DAG = 864e5;
/** Ronde stappen voor de gewichtsas; de kleinste waarmee het bereik in hooguit vier vakken past. */
const STAPPEN = [0.5, 1, 2, 5, 10, 20, 50];
/** Zoveel metingen toont de grafiek hoogstens als hij buiten de gekozen periode moet kijken. */
const MAX_BUITEN = 24;
const JAAR = 365 * DAG;

const utc = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

export function GewichtGrafiek({ punten, tot, maanden, locale, label, leegTekst, buitenPeriodeTekst }: Props) {
  const verloopId = useId();
  const [j, m] = tot.slice(0, 7).split("-").map(Number);
  const periodeStart = Date.UTC(j, m - 1 - (maanden - 1), 1);
  const periodeEind = Date.UTC(j, m, 1);

  const alle = punten
    .map((p) => ({ ...p, t: utc(p.datum) }))
    .filter((p) => !Number.isNaN(p.t))
    .sort((a, b) => a.t - b.t);
  const inPeriode = alle.filter((p) => p.t >= periodeStart && p.t < periodeEind);
  // Niets in de periode maar wel een verleden: laat dat verleden zien in plaats van een leeg vak.
  const buitenPeriode = inPeriode.length === 0 && alle.length > 0;
  const zichtbaar = buitenPeriode ? alle.slice(-MAX_BUITEN) : inPeriode;

  if (zichtbaar.length === 0) {
    return (
      <svg className="pk-grafiek" viewBox={`0 0 ${B} ${H}`} role="img" aria-label={label}>
        <text x={B / 2} y={H / 2} className="pk-grafiek__leeg" textAnchor="middle">
          {leegTekst ?? ""}
        </text>
      </svg>
    );
  }

  const getal = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const maandFmt = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" });
  const maandJaarFmt = new Intl.DateTimeFormat(locale, { month: "short", year: "2-digit", timeZone: "UTC" });
  const jaarFmt = new Intl.DateTimeFormat(locale, { year: "numeric", timeZone: "UTC" });
  const dagFmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" });
  const volFmt = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  const kort = (fmt: Intl.DateTimeFormat, t: number) => fmt.format(t).replace(".", "");

  const bx = B - LINKS - RECHTS;
  const by = H - BOVEN - ONDER;

  // Tijdas: van de eerste tot de laatste meting, minstens twee weken breed, met wat lucht aan beide kanten.
  const eersteT = zichtbaar[0].t;
  const laatsteT = zichtbaar[zichtbaar.length - 1].t;
  const breedte = Math.max(laatsteT - eersteT, 14 * DAG);
  const midden = (eersteT + laatsteT) / 2;
  const xStart = midden - breedte * 0.56;
  const xEind = midden + breedte * 0.56;
  const x = (t: number) => LINKS + ((t - xStart) / (xEind - xStart)) * bx;

  // Gewichtsas: ronde stappen, minstens twee vakken, nooit onder nul.
  const kgs = zichtbaar.map((p) => p.kg);
  const min = Math.min(...kgs);
  const max = Math.max(...kgs);
  const stap = STAPPEN.find((s) => (max - min) / s <= 4) ?? 100;
  let laag = Math.floor(min / stap) * stap;
  let hoog = Math.ceil(max / stap) * stap;
  if (hoog - laag < stap * 2) {
    laag -= stap;
    hoog += stap;
  }
  laag = Math.max(0, laag);
  const y = (kg: number) => BOVEN + (1 - (kg - laag) / (hoog - laag)) * by;
  const lijnen: number[] = [];
  for (let s = laag; s <= hoog + 1e-9; s += stap) lijnen.push(Math.round(s * 10) / 10);

  // Onder de grafiek: maandgrenzen als de metingen over meer maanden lopen, anders de eerste en de laatste datum.
  const spanJaren = (xEind - xStart) / JAAR;
  // Bij een lange reeks springen de labels per kwartaal of per jaar, anders staan ze over elkaar.
  const sprong = spanJaren > 8 ? 24 : spanJaren > 4 ? 12 : spanJaren > 2 ? 6 : spanJaren > 1 ? 3 : 1;
  const maandGrenzen: number[] = [];
  const begin = new Date(xStart);
  for (let t = Date.UTC(begin.getUTCFullYear(), begin.getUTCMonth() + 1, 1); t < xEind; ) {
    maandGrenzen.push(t);
    const d = new Date(t);
    t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + sprong, 1);
  }
  const elkeAndere = maandGrenzen.length > 6;
  // Kijkt de grafiek buiten de periode, dan hoort het jaartal erbij: anders leest "14 mei" als dit jaar.
  const meerJaren = new Date(xStart).getUTCFullYear() !== new Date(xEind).getUTCFullYear() || buitenPeriode;
  const asFmt = spanJaren > 2 ? jaarFmt : meerJaren ? maandJaarFmt : maandFmt;
  const puntFmt = meerJaren ? volFmt : dagFmt;
  const tijdLabels =
    maandGrenzen.length >= 2
      ? maandGrenzen
          .filter((_, i) => !elkeAndere || i % 2 === 0)
          .map((t) => ({ t, tekst: kort(asFmt, t), anker: "middle" as const, grens: true }))
      : [
          { t: eersteT, tekst: kort(puntFmt, eersteT), anker: "start" as const, grens: false },
          ...(laatsteT !== eersteT
            ? [{ t: laatsteT, tekst: kort(puntFmt, laatsteT), anker: "end" as const, grens: false }]
            : []),
        ];

  const lijn = zichtbaar.map((p) => `${x(p.t).toFixed(1)},${y(p.kg).toFixed(1)}`).join(" ");
  const bodem = (BOVEN + by).toFixed(1);
  const vlak =
    zichtbaar.length > 1
      ? `M${x(eersteT).toFixed(1)},${bodem} L${lijn.replace(/ /g, " L")} L${x(laatsteT).toFixed(1)},${bodem} Z`
      : "";

  const laatste = zichtbaar[zichtbaar.length - 1];
  const laatsteX = x(laatste.t);
  const laatsteY = y(laatste.kg);
  // De waarde bij de laatste meting boven het punt; staat het punt tegen de rechterrand, dan links ervan.
  const waardeAnker = laatsteX > B - RECHTS - 14 ? "end" : "middle";

  return (
    <svg className="pk-grafiek" viewBox={`0 0 ${B} ${H}`} role="img" aria-label={label}>
      <defs>
        <linearGradient id={verloopId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--pk-accent)" stopOpacity="0.24" />
          <stop offset="100%" stopColor="var(--pk-accent)" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      <text x={LINKS - 6} y={BOVEN - 9} className="pk-grafiek__eenheid" textAnchor="end">
        kg
      </text>
      {buitenPeriode && buitenPeriodeTekst && (
        <text x={LINKS + 2} y={BOVEN - 9} className="pk-grafiek__notitie" textAnchor="start">
          {buitenPeriodeTekst}
        </text>
      )}
      {lijnen.map((s) => (
        <g key={s}>
          <line x1={LINKS} x2={B - RECHTS} y1={y(s)} y2={y(s)} className="pk-grafiek__raster" />
          <text x={LINKS - 6} y={y(s) + 3.5} className="pk-grafiek__as" textAnchor="end">
            {getal.format(s)}
          </text>
        </g>
      ))}

      {tijdLabels.map((l) => (
        <g key={`${l.t}-${l.tekst}`}>
          {l.grens && <line x1={x(l.t)} x2={x(l.t)} y1={BOVEN} y2={BOVEN + by} className="pk-grafiek__maand" />}
          <text x={x(l.t)} y={H - 6} className="pk-grafiek__as" textAnchor={l.anker}>
            {l.tekst}
          </text>
        </g>
      ))}

      {vlak && <path d={vlak} fill={`url(#${verloopId})`} />}
      {zichtbaar.length > 1 && <polyline points={lijn} className="pk-grafiek__lijn" />}
      {/* Twee wegingen op één dag hebben dezelfde t; de index houdt de key uniek zonder er een weg te laten. */}
      {zichtbaar.slice(0, -1).map((p, i) => (
        <circle key={`${p.t}-${i}`} cx={x(p.t)} cy={y(p.kg)} r={3} className="pk-grafiek__punt">
          <title>{`${kort(volFmt, p.t)}: ${getal.format(p.kg)} kg`}</title>
        </circle>
      ))}
      <circle cx={laatsteX} cy={laatsteY} r={4.5} className="pk-grafiek__punt pk-grafiek__punt--laatste">
        <title>{`${kort(volFmt, laatste.t)}: ${getal.format(laatste.kg)} kg`}</title>
      </circle>
      <text x={laatsteX} y={laatsteY - 9} className="pk-grafiek__waarde" textAnchor={waardeAnker}>
        {getal.format(laatste.kg)}
      </text>
    </svg>
  );
}
